import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Heart } from "lucide-react";
import { api } from "@/lib/api";

export default function ReviewInvite() {
  const { token } = useParams();
  const [info, setInfo] = useState(undefined);
  const [form, setForm] = useState({ name: "", detail: "", text: "", rating: 5, consent: false });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/reviews/invite/${token}`).then((r) => { setInfo(r.data); setForm((f) => ({ ...f, name: r.data.name || "" })); }).catch(() => setInfo(null));
  }, [token]);

  const submit = async () => {
    if (!form.name.trim() || form.text.trim().length < 10) { toast.error("Indiquez votre prénom et quelques mots (10 caractères minimum)"); return; }
    if (!form.consent) { toast.error("Merci d'accepter la publication de votre avis"); return; }
    setBusy(true);
    try { await api.post(`/reviews/invite/${token}`, form); setDone(true); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Envoi impossible"); }
    setBusy(false);
  };

  if (info === undefined) return <p className="max-w-xl mx-auto px-4 py-24 font-sans text-[#1F2A24]/60">Chargement…</p>;
  if (info === null) return <Message testid="review-invite-notfound" title="Invitation introuvable" text="Vérifiez le lien reçu par e-mail." />;
  if (done || info.used) return <Message testid="review-invite-done" title="Merci pour votre avis !" text="Il sera relu avant d'être publié sur le site." />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-14 md:py-20" data-testid="review-invite-page">
      <p className="font-ui text-xs tracking-[0.3em] uppercase text-[#C9A24B]">Votre avis</p>
      <h1 className="font-serif text-4xl text-[#1F2A24] mt-3">Comment s'est passé votre chemin ?</h1>
      <p className="font-sans text-sm text-[#1F2A24]/65 mt-3">Quelques mots suffisent. Ils aideront d'autres lecteurs à se lancer.</p>
      <div className="mt-8 rounded-3xl bg-white border border-[#E8DCC4] p-6 grid gap-4">
        <div className="flex gap-1" data-testid="review-invite-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`} aria-label={`${n} étoiles`} className="p-1 transition-transform hover:scale-110">
              <Star size={28} className="text-[#C9A24B]" fill={n <= form.rating ? "currentColor" : "none"} />
            </button>
          ))}
        </div>
        <textarea rows={5} maxLength={600} value={form.text} onChange={set("text")} placeholder="Ce que le Tao des 4C vous a apporté…" className="border rounded-2xl px-4 py-3 text-sm" data-testid="review-invite-text" />
        <div className="grid sm:grid-cols-2 gap-3">
          <input maxLength={60} value={form.name} onChange={set("name")} placeholder="Prénom affiché *" className="border rounded-full px-4 py-2.5 text-sm" data-testid="review-invite-name" />
          <input maxLength={80} value={form.detail} onChange={set("detail")} placeholder="Ville ou précision (facultatif)" className="border rounded-full px-4 py-2.5 text-sm" data-testid="review-invite-detail" />
        </div>
        <label className="flex items-start gap-2 font-sans text-xs text-[#1F2A24]/70">
          <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5" data-testid="review-invite-consent" />
          J'accepte que mon avis soit publié sur le site avec le prénom indiqué.
        </label>
        <button onClick={submit} disabled={busy} data-testid="review-invite-submit" className="rounded-full bg-[#385E3A] text-white py-3.5 font-ui text-sm hover:bg-[#2c4a2e] disabled:opacity-60">{busy ? "Envoi…" : "Envoyer mon avis"}</button>
      </div>
    </div>
  );
}

const Message = ({ testid, title, text }) => (
  <div className="max-w-md mx-auto px-4 py-28 text-center" data-testid={testid}>
    <Heart className="mx-auto text-[#C9A24B]" size={30} />
    <p className="font-serif text-2xl text-[#1F2A24] mt-3">{title}</p>
    <p className="font-sans text-sm text-[#1F2A24]/60 mt-2">{text}</p>
    <Link to="/" className="inline-block mt-6 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm">Retour à l'accueil</Link>
  </div>
);
